import { useState } from "react";
import { Modal } from "./modal";

export type BoardConflictKind = "changed" | "removed" | "ambiguous";

export interface ConflictDialogProps {
  readonly kind: BoardConflictKind;
  readonly onCancel: () => void;
  readonly onReload: () => Promise<void>;
  readonly onSaveCopy: () => Promise<void>;
}

/** Requires an explicit non-destructive choice after the canonical disk state diverges. */
export function ConflictDialog({ kind, onCancel, onReload, onSaveCopy }: ConflictDialogProps) {
  const [busyAction, setBusyAction] = useState<"reload" | "copy" | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Runs one conflict decision while retaining the in-memory editor after failure. */
  const run = async (action: "reload" | "copy", operation: () => Promise<void>): Promise<void> => {
    setBusyAction(action);
    setError(null);
    try {
      await operation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The board remains safely unchanged.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Modal
      title="Disk copy changed"
      titleId="conflict-title"
      descriptionId="conflict-description"
      onClose={onCancel}
    >
      <div className="decision-dialog">
        <p id="conflict-description" className="modal__lede">
          {conflictExplanation(kind)} Your unsaved editor state is still in this tab. Shotboard will
          not overwrite the disk copy.
        </p>
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <footer className="modal__actions">
          <button
            type="button"
            className="secondary-action"
            disabled={busyAction !== null}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="secondary-action"
            disabled={busyAction !== null}
            onClick={() => void run("reload", onReload)}
          >
            {busyAction === "reload" ? "Reloading…" : reloadLabel(kind)}
          </button>
          <button
            type="button"
            className="primary-action"
            disabled={busyAction !== null}
            onClick={() => void run("copy", onSaveCopy)}
          >
            {busyAction === "copy" ? "Saving copy…" : "Save Current as Copy"}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

/** Explains the observed conflict without claiming an uncertain external cause. */
function conflictExplanation(kind: BoardConflictKind): string {
  if (kind === "removed") {
    return "The board directory or board.json is no longer at its open path.";
  }
  if (kind === "ambiguous") {
    return "The open path disappeared and multiple boards now share this board identity.";
  }
  return "The board.json bytes changed outside this editor after the board was opened.";
}

/** Names the destructive-to-memory option precisely for removed boards. */
function reloadLabel(kind: BoardConflictKind): string {
  return kind === "removed" || kind === "ambiguous" ? "Close Missing Board" : "Reload from Disk";
}
